import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ScraperForm from './components/ScraperForm';
import ResultsTable from './components/ResultsTable';
import GoogleMapsPreview from './components/GoogleMapsPreview';
import ScrapingLogs from './components/ScrapingLogs';
import Login from './components/Login';
import LeadsTab from './components/LeadsTab';
import BatchTab from './components/BatchTab';

function App() {
  // Authentication state - check sessionStorage on mount
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('isAuthenticated') === 'true';
  });

  const [activeTab, setActiveTab] = useState('scrape');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentQuery, setCurrentQuery] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [serverConnected, setServerConnected] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stopRequested, setStopRequested] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5052';
  const abortControllerRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Check server connection on mount
  useEffect(() => {
    const checkServer = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const response = await fetch(`${apiUrl}/health`, {
          method: 'GET',
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          setServerConnected(true);
        } else {
          setServerConnected(false);
        }
      } catch (err) {
        setServerConnected(false);
      }
    };
    checkServer();

    // Recheck every 5 seconds if not connected
    const interval = setInterval(() => {
      if (!serverConnected) {
        checkServer();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [apiUrl, serverConnected]);

  // Handle login
  const handleLogin = () => {
    setIsAuthenticated(true);
    sessionStorage.setItem('isAuthenticated', 'true');
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('isAuthenticated');
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { message, type, timestamp }]);
  };

  const handleScrape = async (formData) => {
    setLoading(true);
    setError(null);
    setResults([]);
    setStats(null);
    setLogs([]); // Clear previous logs
    setStopRequested(false);
    setCurrentQuery(formData.query);
    setCurrentLocation(formData.location);

    addLog(`Starting scrape: ${formData.query} in ${formData.location}`, 'info');
    addLog(`Looking for ${formData.maxResults} results...`, 'info');

    // Prepare abort controller (used only for component unmount/retries, not for stop)
    abortControllerRef.current = new AbortController();

    try {
      // Simulate progress logs while waiting for response
      progressIntervalRef.current = setInterval(() => {
        const messages = [
          'Opening Google Maps...',
          'Loading search results...',
          'Clicking on business cards...',
          'Extracting business details...',
          'Processing contact information...',
          'Extracting email addresses...'
        ];
        const randomMessage = messages[Math.floor(Math.random() * messages.length)];
        addLog(randomMessage, 'debug');
      }, 3000);

      const response = await fetch(`${apiUrl}/api/scrape/maps-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: abortControllerRef.current.signal,
        body: JSON.stringify(formData),
      });

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        addLog(`Error: ${errorData.error || 'Failed to scrape data'}`, 'error');
        throw new Error(errorData.error || 'Failed to scrape data');
      }

      const data = await response.json();
      if (data.cancelled) {
        addLog(`Scrape stopped. Collected ${data.data?.length || 0} businesses.`, 'warning');
      } else {
        addLog(`Successfully scraped ${data.data?.length || 0} businesses`, 'success');
      }
      setResults(data.data || []);
      setStats(data.stats || null);

      // Log summary including duplicates
      const withAddress = data.data?.filter(d => d.address).length || 0;
      const withPhone = data.data?.filter(d => d.phone).length || 0;
      const withWebsite = data.data?.filter(d => d.website).length || 0;
      const withEmail = data.data?.filter(d => d.email).length || 0;

      addLog(`Found: ${withAddress} addresses, ${withPhone} phones, ${withWebsite} websites, ${withEmail} emails`, 'success');

      if (data.stats && data.stats.duplicates > 0) {
        addLog(`Database check: ${data.stats.new} new businesses, ${data.stats.duplicates} already in database`, 'info');
      }
    } catch (err) {
      let errorMessage = err.message;

      // Provide more helpful error messages
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        errorMessage = 'Failed to connect to the server. Please make sure the backend server is running on port 5052.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'The request timed out. The scraping may be taking too long. Try reducing the number of results.';
      } else if (err.message.includes('NetworkError')) {
        errorMessage = 'Network error. Please check your connection and ensure the backend server is running.';
      }

      setError(errorMessage);
      addLog(`Error: ${errorMessage}`, 'error');
      console.error('Scraping error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (formData) => {
    setLoading(true);
    setError(null);
    setLogs([]); // Clear previous logs
    setCurrentQuery(formData.query);
    setCurrentLocation(formData.location);

    addLog(`Starting scrape and download: ${formData.query} in ${formData.location}`, 'info');

    try {
      // Simulate progress logs
      const progressInterval = setInterval(() => {
        const messages = [
          'Scraping Google Maps...',
          'Extracting contact details...',
          'Visiting websites for emails...',
          'Generating Excel file...'
        ];
        const randomMessage = messages[Math.floor(Math.random() * messages.length)];
        addLog(randomMessage, 'debug');
      }, 3000);

      const response = await fetch(`${apiUrl}/api/scrape/maps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json();
        addLog(`Error: ${errorData.error || 'Failed to download spreadsheet'}`, 'error');
        throw new Error(errorData.error || 'Failed to download spreadsheet');
      }

      addLog('Generating spreadsheet...', 'info');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'scraped-businesses.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      addLog('Spreadsheet downloaded successfully!', 'success');
    } catch (err) {
      let errorMessage = err.message;

      // Provide more helpful error messages
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        errorMessage = 'Failed to connect to the server. Please make sure the backend server is running on port 5052.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'The request timed out. The scraping may be taking too long. Try reducing the number of results.';
      } else if (err.message.includes('NetworkError')) {
        errorMessage = 'Network error. Please check your connection and ensure the backend server is running.';
      }

      setError(errorMessage);
      addLog(`Error: ${errorMessage}`, 'error');
      console.error('Download error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setStopRequested(true);
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    try {
      await fetch(`${apiUrl}/api/scrape/maps-stop`, { method: 'POST' });
      addLog('Stop requested. Finishing with partial results...', 'warning');
    } catch (stopError) {
      console.error('Stop request failed:', stopError);
      addLog('Stop request failed', 'error');
    }
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const renderScrapeView = () => (
    <div className="container">
      <h1>Google Scrapper</h1>
      <p className="subtitle">
        Scrape business information from Google Maps and extract contact emails
      </p>

      {serverConnected === null && (
        <div className="info-message">
          Checking server connection...
        </div>
      )}

      {serverConnected === false && (
        <div className="error-message">
          Backend server is not connected. Please start the backend on port 5052.
        </div>
      )}

      {serverConnected === true && (
        <div className="success-message">
          Backend server connected
        </div>
      )}

      <ScraperForm
        onScrape={handleScrape}
        onDownload={handleDownload}
        onStop={handleStop}
        loading={loading}
      />

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <GoogleMapsPreview
        query={currentQuery}
        location={currentLocation}
        visible={loading || results.length > 0}
      />

      <ScrapingLogs
        logs={logs}
        isActive={loading}
      />

      {loading && (
        <div className="stop-wrapper">
          <button className="btn btn-danger" onClick={handleStop}>
            Stop Scrape
          </button>
          {stopRequested && (
            <span className="stop-hint">Finishing current step, returning partial results…</span>
          )}
        </div>
      )}

      {results.length > 0 && (
        <ResultsTable results={results} stats={stats} />
      )}
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'leads') {
      return <LeadsTab apiUrl={apiUrl} />;
    }
    if (activeTab === 'batch') {
      return <BatchTab apiUrl={apiUrl} />;
    }
    return renderScrapeView();
  };

  // Show main app if authenticated
  return (
    <div className="App">
      <div className={`app-shell ${isSidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'}`}>
        <aside className={`app-sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}>
          <div className="sidebar-brand">
            <span className="brand-title">Google Scrapper</span>
            <button
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen(prev => !prev)}
              aria-label="Toggle sidebar"
            >
              {isSidebarOpen ? '⟨' : '⟩'}
            </button>
          </div>
          <nav className="sidebar-nav">
            <button
              className={`sidebar-tab ${activeTab === 'scrape' ? 'active' : ''}`}
              onClick={() => setActiveTab('scrape')}
            >
              <span className="sidebar-icon">●</span>
              <span className="sidebar-label">Scrape</span>
            </button>
            <button
              className={`sidebar-tab ${activeTab === 'leads' ? 'active' : ''}`}
              onClick={() => setActiveTab('leads')}
            >
              <span className="sidebar-icon">■</span>
              <span className="sidebar-label">Leads</span>
            </button>
            <button
              className={`sidebar-tab ${activeTab === 'batch' ? 'active' : ''}`}
              onClick={() => setActiveTab('batch')}
            >
              <span className="sidebar-icon">▤</span>
              <span className="sidebar-label">Batch Process</span>
            </button>
          </nav>
          <div className="sidebar-footer">
            <button
              className="sidebar-logout"
              onClick={handleLogout}
              aria-label="Logout"
            >
              <span className="sidebar-icon">←</span>
              <span className="sidebar-label">Logout</span>
            </button>
          </div>
        </aside>
        <main className="app-main">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default App;
